import type { OverlayPrimitive } from '@limonade/eye-core';

export interface OverlayLayer {
  moduleId: string;
  moduleName: string;
  enabled: boolean;
  overlays: readonly OverlayPrimitive[];
}

export interface OverlayRenderStats {
  renderedPrimitiveCount: number;
  renderedModuleIds: string[];
}

const DEFAULT_COLOR = '#dfff57';
const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));

function pointToCanvas(point: { x: number; y: number }, width: number, height: number) {
  return { x: clamp01(point.x) * width, y: clamp01(point.y) * height };
}

function drawPath(
  context: CanvasRenderingContext2D,
  overlay: OverlayPrimitive,
  width: number,
  height: number,
  close: boolean,
): void {
  const first = overlay.points[0];
  if (!first) return;
  const start = pointToCanvas(first, width, height);
  context.beginPath();
  context.moveTo(start.x, start.y);
  overlay.points.slice(1).forEach((point) => {
    const position = pointToCanvas(point, width, height);
    context.lineTo(position.x, position.y);
  });
  if (close) context.closePath();
}

function drawArrowHead(
  context: CanvasRenderingContext2D,
  start: { x: number; y: number },
  end: { x: number; y: number },
  size: number,
): void {
  const angle = Math.atan2(end.y - start.y, end.x - start.x);
  context.beginPath();
  context.moveTo(end.x, end.y);
  context.lineTo(end.x - size * Math.cos(angle - Math.PI / 6), end.y - size * Math.sin(angle - Math.PI / 6));
  context.lineTo(end.x - size * Math.cos(angle + Math.PI / 6), end.y - size * Math.sin(angle + Math.PI / 6));
  context.closePath();
  context.fill();
}

function drawBox(
  context: CanvasRenderingContext2D,
  overlay: OverlayPrimitive,
  width: number,
  height: number,
  heatmap = false,
): void {
  const first = overlay.points[0];
  const second = overlay.points[1];
  if (!first || !second) return;
  const start = pointToCanvas(first, width, height);
  const end = pointToCanvas(second, width, height);
  const boxWidth = end.x - start.x;
  const boxHeight = end.y - start.y;
  if (overlay.fill || heatmap || overlay.type === 'mask') {
    const originalAlpha = context.globalAlpha;
    context.globalAlpha = Math.min(heatmap ? 0.38 : 0.24, overlay.opacity ?? 0.24);
    context.fillRect(start.x, start.y, boxWidth, boxHeight);
    context.globalAlpha = originalAlpha;
  }
  context.strokeRect(start.x, start.y, boxWidth, boxHeight);
}

function drawText(
  context: CanvasRenderingContext2D,
  overlay: OverlayPrimitive,
  width: number,
  height: number,
): void {
  const point = overlay.points[0];
  if (!point || !overlay.label) return;
  const position = pointToCanvas(point, width, height);
  context.font = '600 12px ui-monospace, SFMono-Regular, monospace';
  context.textBaseline = 'bottom';
  context.lineWidth = 3;
  context.strokeStyle = 'rgba(0, 0, 0, 0.85)';
  context.strokeText(overlay.label, position.x + 7, position.y - 7);
  context.fillText(overlay.label, position.x + 7, position.y - 7);
}

function drawPrimitive(
  context: CanvasRenderingContext2D,
  overlay: OverlayPrimitive,
  width: number,
  height: number,
): boolean {
  if (!overlay.points.length) return false;
  context.save();
  context.strokeStyle = overlay.color ?? DEFAULT_COLOR;
  context.fillStyle = overlay.color ?? DEFAULT_COLOR;
  context.lineWidth = Math.max(1, overlay.stroke_width ?? 2);
  context.globalAlpha = clamp01(overlay.opacity ?? 0.78);
  context.lineJoin = 'round';
  context.lineCap = 'round';

  switch (overlay.type) {
    case 'box':
    case 'rectangle':
      drawBox(context, overlay, width, height);
      break;
    case 'heatmap':
      drawBox(context, overlay, width, height, true);
      break;
    case 'mask':
      if (overlay.points.length === 2) {
        drawBox(context, overlay, width, height);
      } else {
        drawPath(context, overlay, width, height, true);
        context.globalAlpha = Math.min(0.22, overlay.opacity ?? 0.22);
        context.fill();
        context.globalAlpha = clamp01(overlay.opacity ?? 0.78);
        context.stroke();
      }
      break;
    case 'polygon':
      drawPath(context, overlay, width, height, true);
      if (overlay.fill) {
        const originalAlpha = context.globalAlpha;
        context.globalAlpha = Math.min(0.24, overlay.opacity ?? 0.24);
        context.fill();
        context.globalAlpha = originalAlpha;
      }
      context.stroke();
      break;
    case 'arrow':
    case 'line': {
      if (overlay.points.length < 2) break;
      drawPath(context, overlay, width, height, false);
      context.stroke();
      if (overlay.type === 'arrow') {
        const beforeEnd = pointToCanvas(overlay.points[overlay.points.length - 2]!, width, height);
        const end = pointToCanvas(overlay.points[overlay.points.length - 1]!, width, height);
        drawArrowHead(context, beforeEnd, end, Math.max(8, context.lineWidth * 4));
      }
      break;
    }
    case 'vector_field':
      for (let index = 0; index + 1 < overlay.points.length; index += 2) {
        const start = pointToCanvas(overlay.points[index]!, width, height);
        const end = pointToCanvas(overlay.points[index + 1]!, width, height);
        context.beginPath();
        context.moveTo(start.x, start.y);
        context.lineTo(end.x, end.y);
        context.stroke();
        drawArrowHead(context, start, end, Math.max(5, context.lineWidth * 3));
      }
      break;
    case 'point':
    case 'circle': {
      const center = pointToCanvas(overlay.points[0]!, width, height);
      const radius = Math.max(2, (overlay.radius ?? (overlay.type === 'point' ? 0.006 : 0.01)) * Math.min(width, height));
      context.beginPath();
      context.arc(center.x, center.y, radius, 0, Math.PI * 2);
      if (overlay.type === 'point' || overlay.fill) context.fill();
      context.stroke();
      break;
    }
    case 'text':
      drawText(context, overlay, width, height);
      break;
  }
  context.restore();
  return true;
}

/** Draws analyzer-provided primitives without knowing anything about analyzer metrics or algorithms. */
export function renderOverlayLayers(
  canvas: HTMLCanvasElement,
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  layers: readonly OverlayLayer[],
): OverlayRenderStats {
  if (canvas.width !== width) canvas.width = width;
  if (canvas.height !== height) canvas.height = height;
  context.clearRect(0, 0, width, height);

  let renderedPrimitiveCount = 0;
  const renderedModuleIds: string[] = [];
  for (const layer of layers) {
    if (!layer.enabled || !layer.overlays.length) continue;
    let layerRendered = false;
    for (const overlay of layer.overlays) {
      if (drawPrimitive(context, overlay, width, height)) {
        renderedPrimitiveCount++;
        layerRendered = true;
      }
    }
    if (layerRendered) renderedModuleIds.push(layer.moduleId);
  }
  return { renderedPrimitiveCount, renderedModuleIds };
}

export function renderOverlayPrimitives(
  canvas: HTMLCanvasElement,
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  overlays: readonly OverlayPrimitive[],
): void {
  renderOverlayLayers(canvas, context, width, height, [{
    moduleId: 'unscoped',
    moduleName: 'Overlay',
    enabled: true,
    overlays,
  }]);
}
